import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/auth-store';
import { TotpEnrolment } from './totp-enrolment';
import { PageHeader } from '../../components/page-header';
import { Symbol } from '../../components/symbol';

// Signed-in (re-)enrolment page. First-time enrolment no longer happens here:
// 2FA is mandatory, so an admin without a factor is walked through enrolment
// on the login page before any session exists (see TotpEnrolment).
//
//   1. Mount → POST /auth/admin/2fa/setup → backend parks a fresh secret as
//      PENDING (the current factor keeps working) and returns it + the
//      otpauth URL, plus `reenrolment` when a factor is already on.
//   2. Operator scans the QR and enters a code from the new entry — and, when
//      replacing an existing factor, a code from the current one.
//   3. POST /auth/admin/2fa/enable promotes the pending secret.
//   4. On success, force a re-login so the operator proves the new factor
//      works before relying on it.

export function TwoFactorSetupPage() {
  const navigate = useNavigate();
  const session = useAuthStore((s) => s.session);
  const logout = useAuthStore((s) => s.logout);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!session) navigate('/login', { replace: true });
    // Only on mount — a later sign-out is handled by logout() itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!session) return null;

  return (
    <div className="max-w-2xl space-y-6">
      <PageHeader
        title="Set up two-factor auth"
        description="Scan the QR with any authenticator app (Google Authenticator, Authy, 1Password). Then enter the 6-digit code below to confirm. Your current authenticator keeps working until you do."
      />

      {done ? (
        <div role="status" className="alert alert-success">
          <Symbol name="check_circle" size={20} />
          <div>
            <p className="font-semibold">2FA updated</p>
            <p>
              Your new authenticator is now active. You'll be signed out and redirected to the login
              page so you can verify it works.
            </p>
          </div>
        </div>
      ) : (
        <TotpEnrolment
          bearer={session.accessToken}
          onEnabled={() => {
            setDone(true);
            setTimeout(() => void logout(true), 2500);
          }}
        />
      )}
    </div>
  );
}
